import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiAward, 
  FiBookOpen, 
  FiUsers, 
  FiTarget,
  FiCheckCircle,
  FiHeart
} from 'react-icons/fi';
import { useContent } from '../context/ContentContext';
import { getImageUrl } from '../services/api';
import './AboutPage.css';

const AboutPage = () => {
  const { content } = useContent();

  // İkon eşleştirme
  const iconMap = {
    heart: FiHeart,
    target: FiTarget,
    users: FiUsers,
    book: FiBookOpen,
    award: FiAward,
    check: FiCheckCircle
  };

  // Varsayılan değerler
  const defaultValues = [
    { icon: 'heart', title: 'Öğrenci Odaklılık', desc: 'Her öğrencinin benzersiz olduğuna inanır, bireysel gelişimi destekleriz.' },
    { icon: 'target', title: 'Başarı Odaklılık', desc: 'Hedeflerimiz büyük, azimle çalışır ve başarıya ulaşırız.' },
    { icon: 'users', title: 'Takım Ruhu', desc: 'Öğretmenler, öğrenciler ve veliler olarak birlikte çalışırız.' },
    { icon: 'book', title: 'Sürekli Gelişim', desc: 'Eğitim metodlarımızı sürekli güncel tutarız.' }
  ];

  const defaultMilestones = [
    { year: '2009', title: 'Kuruluş', desc: 'BMK Eğitim Kurumları kuruldu' },
    { year: '2012', title: 'Büyüme', desc: 'İkinci şubemizi açtık' },
    { year: '2015', title: 'Genişleme', desc: 'Online eğitim platformu başladı' },
    { year: '2018', title: 'Yenilik', desc: 'Modern teknoloji sınıfları' },
    { year: '2024', title: 'Bugün', desc: '5000+ mezun, %95 başarı' }
  ];

  // Varsayılan Neden Biz listesi
  const defaultWhyUs = [
    { title: 'Uzman Kadro', desc: 'Alanında en az 10 yıl deneyimli öğretmenler' },
    { title: 'Küçük Sınıflar', desc: 'Maksimum 15 kişilik sınıflarla birebir ilgi' },
    { title: 'Modern Teknoloji', desc: 'Akıllı tahta ve online takip sistemi' },
    { title: 'Düzenli Takip', desc: 'Haftalık raporlar ve veli görüşmeleri' },
    { title: 'Rehberlik', desc: 'Profesyonel rehberlik ve kariyer danışmanlığı' }
  ];

  // Content'ten veya varsayılanlardan al
  const values = (content.about?.values || defaultValues).map(v => ({
    ...v,
    icon: iconMap[v.icon] || FiHeart
  }));
  const milestones = content.about?.milestones || defaultMilestones;
  const whyUsList = content.about?.whyUs || defaultWhyUs;

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg"></div>
        <div className="container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>{content.about?.heroTitle || 'Hakkımızda'}</h1>
            <p>{content.about?.heroSubtitle || '15 yılı aşkın tecrübemizle geleceğin liderlerini yetiştiriyoruz'}</p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="about-story section">
        <div className="container">
          <div className="story-grid">
            <motion.div 
              className="story-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">{content.about?.title || 'Hikayemiz'}</h2>
              <div className="story-description">
                {content.about?.description || 'BMK Eğitim Kurumları, 2009 yılında eğitime gönül vermiş bir grup öğretmen tarafından kuruldu. Amacımız, her öğrencinin potansiyelini en üst düzeye çıkarmak ve hayallerindeki geleceğe ulaşmalarına yardımcı olmaktı.'}
              </div>
              <div className="story-stats">
                {(content.stats?.items || [
                  { number: '15+', label: 'Yıllık Tecrübe' },
                  { number: '50+', label: 'Uzman Öğretmen' },
                  { number: '5000+', label: 'Mezun' }
                ]).slice(0, 3).map((stat, index) => (
                  <div key={index} className="story-stat">
                    <span className="stat-value">{stat.number}</span>
                    <span className="stat-text">{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div 
              className="story-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {content.about?.image ? (
                <img src={getImageUrl(content.about.image)} alt="Hakkımızda" className="about-image" />
              ) : (
                <div className="image-placeholder">
                  <FiUsers />
                  <span>Ekibimiz</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section section section-dark">
        <div className="container">
          <div className="text-center">
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {content.about?.valuesTitle || 'Değerlerimiz'}
            </motion.h2>
            <motion.p 
              className="section-subtitle"
              style={{ color: 'rgba(255,255,255,0.8)', margin: '0 auto 3rem' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {content.about?.valuesSubtitle || 'Eğitim anlayışımızın temelini oluşturan değerler'}
            </motion.p>
          </div>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div 
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="value-icon">
                  <value.icon />
                </div>
                <h3>{value.title}</h3>
                <p>{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section section">
        <div className="container">
          <div className="text-center">
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {content.about?.milestonesTitle || 'Yolculuğumuz'}
            </motion.h2>
          </div>

          <div className="timeline">
            {milestones.map((milestone, index) => (
              <motion.div 
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <span className="timeline-year">{milestone.year}</span>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="why-us section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="why-us-grid">
            <motion.div 
              className="why-us-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">{content.about?.whyUsTitle || 'Neden Biz?'}</h2>
              <ul className="why-us-list">
                {whyUsList.map((item, index) => (
                  <li key={index}>
                    <FiCheckCircle />
                    <div>
                      <h4>{typeof item === 'string' ? item : item.title}</h4>
                      {typeof item !== 'string' && item.desc && <p>{item.desc}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div 
              className="why-us-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {content.about?.whyUsImage ? (
                <img src={getImageUrl(content.about.whyUsImage)} alt="Neden Biz" className="about-image" />
              ) : (
                <div className="image-placeholder">
                  <FiAward />
                  <span>Başarı</span>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;

